const ShiftPatternService = {

  /**
   * Sets default values if a day is not already present
   * 
   * @param {Array<Object>} days
   * @param {Object} selectedDays
   * @param {Object} selectedShiftsByDay
   * @param {function} set
   */
  initDays(days, selectedDays, selectedShiftsByDay, set) {
    days.forEach(day => {
      if (!(day.id in selectedDays)) set(selectedDays, day.id, false);
      if (!(day.id in selectedShiftsByDay)) set(selectedShiftsByDay, day.id, []);
    });
  },

  /**
   * Format a time string to 'HH:mm'
   * 
   * @param {string} time
   * @returns {string} formatted time
   */
  changeTimeFormat(time) {
    if (!time) return '';
    const [hourString, minuteString] = time.split(':');
    let hour = parseInt(hourString, 10);
    const period = hour >= 12 ? 'PM' : 'AM'
    hour = hour % 12;
    hour = hour === 0 ? 12 : hour;
    return `${hour}:${minuteString} ${period}`;
  },

  /**
   * Mark selected days and shifts based on existing patterns
   * 
   * @param {Array<Object>} patterns
   * @param {Object} selectedDays
   * @param {Object} selectedShiftsByDay
   * @param {function} set
   */
  defineCheckForDayAndShifts(patterns, selectedDays, selectedShiftsByDay, set) {
    patterns.forEach(item => {
      const dayIds = item.days.split(',').map(Number);
      dayIds.forEach(dayId => {
        set(selectedDays, dayId, true);
        if (!selectedShiftsByDay[dayId]) {
          set(selectedShiftsByDay, dayId, [])
        }
        if (!selectedShiftsByDay[dayId].includes(item.shift_id)) {
          selectedShiftsByDay[dayId].push(item.shift_id);
        }
      })
    }) 
  },

  /**
   * Check days uncheck error when checked shift
   * 
   * @param {number|string} dayId
   * @param {Object} selectedDays
   * @param {Object} selectedShiftsByDay
   * @param {boolean} submitted
   * @returns {boolean}
   */
  dayHasError(dayId, selectedDays, selectedShiftsByDay, submitted) {
    if (!selectedShiftsByDay || !selectedDays) return false;
    const shifts = selectedShiftsByDay[dayId] || [];
    const dayChecked = selectedDays[dayId] || false;
    return (
      submitted &&
      shifts.length > 0 &&
      !dayChecked
    );
  },

  /**
   * Check shift uncheck error when checked days
   * 
   * @param {number|string} dayId
   * @param {Object} selectedDays
   * @param {Object} selectedShiftsByDay
   * @param {boolean} submitted
   * @returns {boolean}
   */
  shiftHasError(dayId, selectedDays, selectedShiftsByDay, submitted) {
    if (!selectedShiftsByDay || !selectedDays) return false;
    const shifts = selectedShiftsByDay[dayId] || [];
    const dayChecked = selectedDays[dayId] || false;
    return (
      submitted &&
      !shifts.length &&
      dayChecked
    );
  },

  /**
   * Check any shift is selected 
   *
   * @param {Object} selectedShiftsByDay
   * @returns {boolean}
   */
  hasAnyShiftSelected(selectedShiftsByDay) {
    return Object.values(selectedShiftsByDay).some(shifts => shifts.length > 0);
  },

  /**
   * Check any shift is selected 
   *
   * @param {Object} selectedDays
   * @returns {boolean}
   */
  hasAnyDaySelected(selectedDays) {
    return Object.values(selectedDays).some(checked => checked === true);
  },

  /**
   * Prepare the data for sending request
   *
   * @param {Object} selectedShiftsByDay
   * @param {Array} shiftLists
   * @returns {Array<Object>}
   */
  prepareRequestData(selectedShiftsByDay, shiftLists) {
    const shiftDayMap = {};
    Object.keys(selectedShiftsByDay).forEach(dayId => {
      const shifts = selectedShiftsByDay[dayId];
      shifts.forEach(shiftId => {
        if (!shiftDayMap[shiftId]) {
          shiftDayMap[shiftId] = [];
        }
        shiftDayMap[shiftId].push(dayId);
      });
    });

    const orderedShiftIds = shiftLists
      .map(shift => shift.id)
      .filter(id => shiftDayMap[id]);

    return orderedShiftIds.map(shiftId => ({
      shift_id: parseInt(shiftId),
      days: shiftDayMap[shiftId].join(',')
    }));
  },

  /**
   * Check if days is check, auto check all shifts under that day
   * 
   * @param {number|string} dayId
   * @param {Object} selectedDays
   * @param {Object} selectedShiftsByDay
   * @param {array} shiftLists
   * @param {function} set
   * @returns {void}
   */
  defineDayChange(dayId, selectedDays, selectedShiftsByDay, shiftLists, set) {
    const isChecked = selectedDays[dayId];
    if (isChecked) {
      set(selectedShiftsByDay, dayId, []);
    }
  },

  /**
   * Check if shift is check, auto check that shift of day
   * 
   * @param {number|string} dayId
   * @param {Object} selectedDays
   * @param {Object} selectedShiftsByDay
   * @param {function} set
   * @returns {void}
   */
  defineShiftChange(dayId, selectedDays, selectedShiftsByDay, set) {
    const selectedShifts = selectedShiftsByDay[dayId] || [];
    if (selectedShifts.length > 0) {
      set(selectedDays, dayId, true);
    } else {
      set(selectedDays, dayId, false);
    }
  },

  /**
   * Check each day and shift have overlap 
   * 
   * @param {number} shiftId
   * @param {number|string} dayId
   * @param {array} shiftErrorData
   * @param {Object} selectedShiftsByDay
   * @returns {boolean}
   */
  defineShiftOverlapped(shiftId, dayId, shiftErrorData, selectedShiftsByDay) {
    if (!shiftErrorData || !Array.isArray(shiftErrorData)) return false;
    const isSelected = selectedShiftsByDay[dayId] && 
                      selectedShiftsByDay[dayId].includes(shiftId);

    if (!isSelected) return false;

    return shiftErrorData.some(errorObj => {
      return errorObj.date == dayId && errorObj.ids.includes(shiftId);
    });
  },

  /**
   * Define each specific overlap error message
   * 
   * @param {number} shiftId
   * @param {number|string} dayId
   * @param {Object} shiftErrorData
   * @returns {string}
   */
  defineOverlapErrMsg(shiftId, dayId, shiftErrorData) {
    const error = shiftErrorData.find(err => 
      err.date == dayId && err.ids.includes(shiftId)
    );
      return error ? error.msg : '';
  },
}

export default ShiftPatternService;
